import { navigate } from 'gatsby';
import React, { useEffect } from 'react';
import { toggleApply } from '../../app/redux/actions/globalModalAction';
import updateLastAction from '../../app/redux/actions/updateLastAction';
import { setUserprofileCompletionState } from '../../app/redux/actions/userProfileCompletionActions';
import { useAppDispatch, useAppSelector } from '../../app/redux/hooks';
import { AUTH_LEVEL, ActionType } from '../../app/services/authTypes';
import { Opportunity } from '../../types/content';
import OnBoardingWizard from '../account/OnBoardingWizard';
import ProfileQuestionaryWidget from '../forms/complete/ProfileQuestionaryWidget';
import { ApplyModal } from './ApplyModal';
import GlobalModal from './GlobalModal';
import { useGlobalModal } from './useGlobalModal';

type Props = {
  location: any;
};

const ModalWrapper = ({ location }: Props) => {
  const { setConfig, disableClose, open } = useGlobalModal();
  const { modalConfig, applyModalStatus, applyOpportunity } = useAppSelector((state) => state.globalModal);

  const dispatch = useAppDispatch();
  const { isSignedIn, user, authLevel } = useAppSelector((state) => state.auth);
  const { userprofileCompletionState } = useAppSelector((state) => state.profileCompletion);
  const isProfileCompletion = typeof user?.profileCompletion === 'number';

  const handleOnCloseProfileCompletionModal = () => {
    dispatch(setUserprofileCompletionState(false));
  };

  /**
   * the profile completion by email must be trigger only once
   * and can't have userprofileCompletionState as dependency
   * array to avoid infinitly setting the state to true
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isCompleteProfile = params.has('isCompleteProfile');
    const usernameParam = params.get('username');

    if ((!isSignedIn || (isSignedIn && authLevel === AUTH_LEVEL.PARTIAL)) && isCompleteProfile) {
      dispatch(
        updateLastAction(location, ActionType.PROFILE_COMPLETION_EMAIL, {
          username: usernameParam
        })
      );
      navigate('/login');
    }

    if (isCompleteProfile) {
      setConfig({ fullScreen: true });
      dispatch(setUserprofileCompletionState(true));
    }
  }, []);

  // conditions to open the profile completion modal
  useEffect(() => {
    if (isSignedIn && userprofileCompletionState) {
      openCompleteProfileModal();
    }
  }, [isSignedIn, userprofileCompletionState, isProfileCompletion]);

  useEffect(() => {
    if (applyModalStatus && applyOpportunity != undefined) {
      openApplyModal();
    }
  }, [applyModalStatus]);

  // profile completion modal configuration
  const openCompleteProfileModal = () => {
    setConfig({
      isOverlay: true,
      type: 'profile',
      dialogClasses: 'max-w-2xl'
    });
    open();
  };

  // profile attribution modal configuration
  const openAttributionModal = () => {
    setConfig({
      isOverlay: true,
      type: 'attribution',
      fullScreen: false
    });
    open();
  };

  // Apply modal configuration
  const openApplyModal = () => {
    setConfig({
      isOverlay: true,
      type: 'apply',
      minheightContent: 'h-auto',
      minWidthContent: 'w-96',
      fullScreen: false
    });
    open();
    disableClose();
  };

  if (modalConfig) {
    switch (modalConfig.type) {
      case 'profile':
        return (
          <GlobalModal onClose={handleOnCloseProfileCompletionModal}>
            <ProfileQuestionaryWidget />
          </GlobalModal>
        );
        break;
      case 'dashboard-onboarding':
        return (
          <GlobalModal>
            <OnBoardingWizard firstName={user?.firstName} />
          </GlobalModal>
        );
        break;
      case 'apply':
        return (
          <GlobalModal
            onClose={() => {
              dispatch(toggleApply(false));
            }}
          >
            <ApplyModal opportunity={applyOpportunity as Opportunity} />
          </GlobalModal>
        );
    }
  }

  return null;
};

export default ModalWrapper;
